import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiShieldLockOutline } from '@mdi/js'
import { AuthLayout } from '../components/auth/AuthLayout'
import { TotpEnrollment } from '../components/auth/TotpEnrollment'
import { CodeFactorEnrollment } from '../components/auth/CodeFactorEnrollment'
import { Button } from '../components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import { AppIcon } from '../components/ui/icon'
import { useAuth } from '../hooks/useAuth'
import { fetchMfaStatus, type MFAConfirmResult } from '../lib/mfa'
import { safeNextPath } from '../lib/safeNextPath'

interface MfaSetupSearch {
    org?: string
    next?: string
}

// The full-screen forced enrollment (spec 2026-10-01-03): a member who must
// use two-step verification but has no factor lands here before any other
// route, with a session the backend restricts to this page's endpoints.
// Rendered without the app chrome (routes/__root.tsx isBareRoute): the
// sidebar and live events would only collect 403s.
export const Route = createFileRoute('/mfa-setup')({
    component: MfaSetupPage,
    validateSearch: (search: Record<string, unknown>): MfaSetupSearch => ({
        org: (search.org as string) || undefined,
        next: (search.next as string) || undefined,
    }),
})

function MfaSetupPage() {
    const { t } = useTranslation('auth')
    const navigate = useNavigate()
    const { org: orgParam, next } = Route.useSearch()
    const { isAuthenticated, orgCode, logout, applyUpgradedAccessToken, user } = useAuth()
    const org = orgParam || orgCode || 'demo'
    const [confirmed, setConfirmed] = useState<MFAConfirmResult | null>(null)
    const [chosen, setChosen] = useState<'totp' | 'email' | 'sms' | null>(null)

    useEffect(() => {
        if (!isAuthenticated) {
            navigate({ to: '/$org/login', params: { org }, search: { next: next || undefined, email: undefined, password: undefined } })
        }
    }, [isAuthenticated, navigate, org, next])

    const { data: status } = useQuery({
        queryKey: ['mfa-status', org],
        queryFn: () => fetchMfaStatus(org),
        enabled: isAuthenticated,
    })

    const onConfirmed = (result: MFAConfirmResult) => {
        if (result.access_token) {
            applyUpgradedAccessToken(result.access_token, result.expires_in ?? 3600)
        }
        setConfirmed(result)
    }

    const proceed = () => {
        const target = safeNextPath(next)
        if (target && !target.startsWith('/mfa-')) {
            window.location.assign(target)
            return
        }
        navigate({ to: '/$org', params: { org } })
    }

    const totpAllowed = !status || status.enrollable_factors.includes('totp')
    const passkeyAllowed = status?.enrollable_factors.includes('passkey')
    // Every kind enrollable here, the authenticator app first (spec
    // 2026-10-01-04 adds e-mail and SMS codes).
    const methods = (['totp', 'email', 'sms'] as const).filter((k) =>
        k === 'totp' ? totpAllowed : status?.enrollable_factors.includes(k))
    const method = chosen && methods.includes(chosen) ? chosen : methods[0]

    return (
        // The bare shell has no flex column for AuthLayout's flex-1 to fill.
        <div className="flex min-h-svh flex-col">
        <AuthLayout>
            <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-dialog" data-testid="mfa-setup-page">
                <div className="flex items-center gap-3">
                    <AppIcon path={mdiShieldLockOutline} size="lg" className="text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">
                        {confirmed ? t('mfa.setup.done_title') : t('mfa.setup.title')}
                    </h2>
                </div>

                {confirmed ? (
                    <div className="mt-6 space-y-6" data-testid="mfa-setup-done">
                        <p className="text-sm text-muted-foreground">{t('mfa.setup.done_description')}</p>
                        <Button className="w-full" onClick={proceed} data-testid="mfa-setup-continue">
                            {t('mfa.setup.continue')}
                        </Button>
                    </div>
                ) : (
                    <div className="mt-4 space-y-4">
                        {(status?.required || status?.enrollment_only) && (
                            <p className="text-sm text-foreground" data-testid="mfa-setup-required">
                                {t('mfa.setup.required')}
                            </p>
                        )}
                        {methods.length > 1 && method && (
                            <Tabs value={method} onValueChange={(v) => setChosen(v as typeof method)}>
                                <TabsList className="w-full" data-testid="mfa-setup-methods">
                                    {methods.map((k) => (
                                        <TabsTrigger key={k} value={k} className="flex-1 text-xs" data-testid={`mfa-setup-method-${k}`}>
                                            {t(`mfa.factor_kinds.${k}`)}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>
                        )}
                        {method === 'totp' ? (
                            <TotpEnrollment org={org} onConfirmed={onConfirmed} />
                        ) : method ? (
                            <CodeFactorEnrollment key={method} org={org} kind={method} email={user?.email} onConfirmed={onConfirmed} />
                        ) : (
                            <p className="text-sm text-muted-foreground" data-testid="mfa-setup-nothing">
                                {t('mfa.setup.nothing_enrollable')}
                            </p>
                        )}
                        {passkeyAllowed && method && (
                            <p className="text-xs text-muted-foreground">{t('mfa.setup.passkey_hint')}</p>
                        )}
                    </div>
                )}

                <div className="mt-6 text-center text-sm">
                    <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                        onClick={() => logout(org)}
                        data-testid="mfa-setup-sign-out"
                    >
                        {t('mfa.setup.sign_out')}
                    </button>
                </div>
            </div>
        </AuthLayout>
        </div>
    )
}
